import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { exchangeSession } from "../../api/client";
import { createMatrixClient, startClientAndWaitForSync, stopMatrixClient } from "../../matrix/client";
import type { SessionExchangeData } from "../../api/types";
import { readSessionToken } from "./token";
import { loadStoredSession, saveStoredSession, clearStoredSession } from "./sessionStore";

/**
 * Remove the one-time `?s=` token from the address bar after a successful
 * exchange so it isn't left lingering or re-used on refresh. Skipped in tests
 * (where `search` is injected) to avoid touching real browser history.
 */
function stripTokenFromUrl(searchOverride?: string): void {
  if (searchOverride !== undefined) return;
  try {
    if (typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname + window.location.hash);
    }
  } catch {
    // ignore — non-fatal
  }
}

export type SessionPhase = "no-session" | "connecting" | "ready" | "error";

export interface SessionState {
  phase: SessionPhase;
  client: MatrixClient | null;
  userId: string | null;
  /** Matrix access token — needed as Bearer for provisioning-API user search. */
  accessToken: string | null;
  error: string | null;
}

const initialState: SessionState = {
  phase: "connecting",
  client: null,
  userId: null,
  accessToken: null,
  error: null,
};

const SessionContext = createContext<SessionState>(initialState);

export function useSession(): SessionState {
  return useContext(SessionContext);
}

interface SessionProviderProps {
  children: ReactNode;
  /** Overridable for tests. Defaults to window.location.search. */
  search?: string;
}

/**
 * Owns the whole session lifecycle: read `?s=`, exchange it for Matrix
 * credentials, create the (crypto-free) client, and wait for first sync.
 * The Matrix session is kept in memory only (iframe-friendly).
 */
export function SessionProvider({ children, search }: SessionProviderProps) {
  const [state, setState] = useState<SessionState>(initialState);
  const clientRef = useRef<MatrixClient | null>(null);

  useEffect(() => {
    const query = search ?? window.location.search;
    const token = readSessionToken(query);
    const stored = loadStoredSession();

    if (!token && !stored) {
      setState({ phase: "no-session", client: null, userId: null, accessToken: null, error: null });
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    setState({ phase: "connecting", client: null, userId: null, accessToken: null, error: null });

    (async () => {
      try {
        // Prefer a NEW secure link (a `?s=` that differs from the one that
        // produced the stored session); otherwise restore the persisted Matrix
        // credentials so a refresh doesn't re-exchange the single-use token.
        let creds: SessionExchangeData;
        if (token && (!stored || stored.sourceToken !== token)) {
          creds = await exchangeSession(token, controller.signal);
          saveStoredSession({ creds, sourceToken: token });
          stripTokenFromUrl(search);
        } else if (stored) {
          creds = stored.creds;
        } else {
          creds = await exchangeSession(token as string, controller.signal);
          saveStoredSession({ creds, sourceToken: token as string });
          stripTokenFromUrl(search);
        }
        if (cancelled) return;

        const client = createMatrixClient(creds);
        clientRef.current = client;

        await startClientAndWaitForSync(client, { initialSyncLimit: 30 });
        if (cancelled) {
          stopMatrixClient(client);
          return;
        }

        setState({
          phase: "ready",
          client,
          userId: creds.userId,
          accessToken: creds.accessToken,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        // Whatever we tried failed — drop any persisted creds so the next load
        // starts clean (a fresh link) instead of retrying stale credentials.
        clearStoredSession();
        const message =
          err instanceof Error ? err.message : "Could not start your chat session.";
        setState({ phase: "error", client: null, userId: null, accessToken: null, error: message });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      if (clientRef.current) {
        stopMatrixClient(clientRef.current);
        clientRef.current = null;
      }
    };
  }, [search]);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}
